import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import "./PredefinedInput.css";

/**
 * Prompt picker for predefined-questions mode (Figma 6153-264).
 *
 * The trigger sits inside the chat input bar; clicking it opens a white
 * panel above the bar spanning its full width. After a question with
 * follow-ups is selected, the panel shows two sections:
 *
 *   - "Additional Information" — children of the last-selected question
 *   - "New topic"              — the root-level questions
 *
 * Clicking a question sends its text (same `onQuestionClick` contract as
 * before). If it has children, the next open starts at its sub-level.
 * The chevron on a row drills into its children without sending.
 */
const PredefinedInput = ({ questions, onQuestionClick, disabled }) => {
  const [open, setOpen] = useState(false);
  const [currentParentId, setCurrentParentId] = useState(null);
  const rootRef = useRef(null);

  const safeQuestions = useMemo(() => questions || [], [questions]);

  const byOrder = (a, b) => a.order - b.order;
  const followUps = useMemo(
    () =>
      currentParentId === null
        ? []
        : safeQuestions.filter((q) => q.parent_id === currentParentId).sort(byOrder),
    [safeQuestions, currentParentId],
  );
  const rootQuestions = useMemo(
    () => safeQuestions.filter((q) => q.parent_id === null).sort(byOrder),
    [safeQuestions],
  );

  const close = useCallback(() => setOpen(false), []);

  // Click outside / Escape closes the panel.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const hasChildren = (question) =>
    safeQuestions.some((child) => child.parent_id === question.id);

  const selectQuestion = (question) => {
    onQuestionClick(question.text);
    // Same behaviour as the previous picker: selecting a question with
    // follow-ups positions the next open at that sub-level; leaf
    // selections reset back to the root.
    setCurrentParentId(hasChildren(question) ? question.id : null);
    close();
  };

  // Browse into a question's follow-ups without sending it.
  const drillInto = (question) => (e) => {
    e.stopPropagation();
    setCurrentParentId(question.id);
  };

  const renderItem = (question) => (
    <div key={question.id} className="prompt-picker__row">
      <button
        type="button"
        className="prompt-picker__item"
        onClick={() => selectQuestion(question)}
      >
        {question.text}
      </button>
      {hasChildren(question) && (
        <button
          type="button"
          className="prompt-picker__drill"
          aria-label={`View follow-up prompts for: ${question.text}`}
          onClick={drillInto(question)}
        >
          <FiChevronRight />
        </button>
      )}
    </div>
  );

  return (
    <div className="prompt-picker" ref={rootRef}>
      <button
        type="button"
        className="prompt-picker__trigger"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
      >
        <span>Click to select a prompt</span>
        <FiChevronDown
          aria-hidden="true"
          className={`prompt-picker__chevron ${open ? "prompt-picker__chevron--open" : ""}`}
        />
      </button>

      {open && (
        <div className="prompt-picker__panel">
          {followUps.length > 0 && (
            <>
              <p className="prompt-picker__section">Additional Information</p>
              {followUps.map(renderItem)}
            </>
          )}
          {followUps.length > 0 && rootQuestions.length > 0 && (
            <p className="prompt-picker__section">New topic</p>
          )}
          {rootQuestions.map(renderItem)}
        </div>
      )}
    </div>
  );
};

export default PredefinedInput;
